import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { auth } from '../../firebase';

// Use Builder Service API (not legacy webhook proxy)
const BUILDER_API_URL = import.meta.env.VITE_BUILDER_API_URL || 'https://api.segmently.ai';

// MUI Imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

// Icons
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DevicesIcon from '@mui/icons-material/Devices';
import PublicIcon from '@mui/icons-material/Public';
import LayersIcon from '@mui/icons-material/Layers';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';

import { ShadowTag } from './usageShared';

/**
 * Traffic Quality panel — what the raw request volume was made of, and how
 * solid the billable count is (JS-verification shadow split).
 * Theme tokens only; composition counts raw requests, billable counts unique
 * visitors — the two scales differ by design (footnote states this).
 */
function TrafficQualityPanel({ details }) {
  const total = details?.totalRequests || 0;
  const bots = details?.botRequests || 0;
  const errors = details?.errorRequests || 0;
  const articles = details?.articleRequests || 0;
  const eligible = Math.max(total - bots - errors - articles, 0);
  const billable = details?.billableRequests || 0;
  const verified = details?.verifiedRequests || 0;
  const unverified = Math.max(billable - verified, 0);

  const pct = (n, base) => (base > 0 ? (n / base) * 100 : 0);
  const pctLabel = (n, base) => `${pct(n, base).toFixed(1)}%`;

  const segments = [
    { key: 'eligible', label: 'Billable-eligible requests', count: eligible, color: 'success.main' },
    { key: 'bots', label: 'Bot traffic (excluded)', count: bots, color: 'warning.main' },
    { key: 'errors', label: 'Errors / unpublished (excluded)', count: errors, color: 'grey.500' },
    { key: 'articles', label: 'Article views (not billed)', count: articles, color: 'info.main' },
  ];

  const botSourcesCaption = Object.entries(details?.botSources || {})
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([source, count]) => `${source} ${count.toLocaleString()}`)
    .join(' · ');

  return (
    <Paper sx={{ p: 2, mb: 3 }} data-testid="usage-traffic-quality-panel">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ShieldOutlinedIcon color="primary" />
        <Typography variant="h6">Traffic Quality</Typography>
      </Box>
      <Typography variant="caption" color="text.secondary">
        What the {total.toLocaleString()} requests were made of, and how solid the billable count is.
      </Typography>

      {/* Request composition bar */}
      <Box sx={{ display: 'flex', height: 14, borderRadius: 1, overflow: 'hidden', my: 1.5 }}>
        {segments.filter((s) => s.count > 0).map((s) => (
          <Box key={s.key} sx={{ width: `${pct(s.count, total)}%`, bgcolor: s.color }} />
        ))}
        {total === 0 && <Box sx={{ width: '100%', bgcolor: 'divider' }} />}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 4, rowGap: 0.5 }}>
        {segments.map((s) => (
          <Box key={s.key}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color, flexShrink: 0, alignSelf: 'center' }} />
              <Typography variant="body2">{s.label}</Typography>
              <Typography variant="body2" fontWeight="medium" sx={{ ml: 'auto' }}>
                {s.count.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ width: 48, textAlign: 'right' }}>
                {pctLabel(s.count, total)}
              </Typography>
            </Box>
            {s.key === 'bots' && botSourcesCaption && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 2.25 }}>
                {botSourcesCaption}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      {/* Verification of billable impressions (shadow) */}
      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" fontWeight="medium">
          Verification of billable impressions
        </Typography>
        <ShadowTag />
      </Box>
      <Box sx={{ display: 'flex', height: 10, borderRadius: 1, overflow: 'hidden', my: 1 }}>
        <Box sx={{ width: `${pct(verified, billable)}%`, bgcolor: 'info.main' }} />
        <Box sx={{ flex: 1, bgcolor: 'divider' }} />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 4, rowGap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'info.main', flexShrink: 0, alignSelf: 'center' }} />
          <Typography variant="body2">JS-verified visitors</Typography>
          <Typography variant="body2" fontWeight="medium" sx={{ ml: 'auto' }}>
            {verified.toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ width: 48, textAlign: 'right' }}>
            {pctLabel(verified, billable)}
          </Typography>
        </Box>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'divider', flexShrink: 0, alignSelf: 'center' }} />
            <Typography variant="body2">No client event received</Typography>
            <Typography variant="body2" fontWeight="medium" sx={{ ml: 'auto' }}>
              {unverified.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ width: 48, textAlign: 'right' }}>
              {pctLabel(unverified, billable)}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 2.25 }}>
            JS blocked, bounced pre-hydration, or non-executing bot — will not be billed once the
            shadow criterion activates
          </Typography>
        </Box>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 1.5, pt: 1, borderTop: 1, borderColor: 'divider' }}
      >
        Composition counts raw requests; billable impressions count unique visitors per funnel —
        the two scales differ by design.
      </Typography>
    </Paper>
  );
}

/**
 * ProjectUsageDetails - Detailed usage view for a specific project
 *
 * Shows daily stats, platform breakdown, country breakdown, and top funnels.
 * Reusable component that can later be shown to project owners.
 */
export default function ProjectUsageDetails() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  // Data state
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state (initialized from URL params or defaults)
  const [startDate, setStartDate] = useState(() => {
    return searchParams.get('startDate') || (() => {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    })();
  });
  const [endDate, setEndDate] = useState(() => {
    return searchParams.get('endDate') || new Date().toISOString().split('T')[0];
  });
  const [environment, setEnvironment] = useState(() => {
    return searchParams.get('environment') || 'prod';
  });

  // Check if user is admin (for now - later can add project owner check)
  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">You do not have permission to view project usage details.</Alert>
      </Box>
    );
  }

  /**
   * Fetch project usage details from the backend API
   */
  const fetchDetails = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const params = new URLSearchParams({
        startDate,
        endDate,
        environment,
      });

      const response = await fetch(`${BUILDER_API_URL}/api/usage/projects/${projectId}?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      // API returns { success, data: {...}, meta: {...} }
      setDetails(data.data);
    } catch (err) {
      console.error('Failed to fetch project usage details:', err);
      setError(err.message || 'Failed to load usage details');
    } finally {
      setLoading(false);
    }
  }, [projectId, startDate, endDate, environment]);

  // Fetch data on mount and when filters change
  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  /**
   * Format number with thousands separator
   */
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };

  // Period totals come from the summary query (period-accurate DISTINCT counts);
  // summing daily DISTINCT values would overcount returning visitors across days.
  const totals = {
    totalRequests: details?.totalRequests ?? 0,
    billableRequests: details?.billableRequests ?? 0,
    verifiedRequests: details?.verifiedRequests ?? 0,
    uniqueUsers: details?.uniqueUsers ?? 0,
  };
  const verifiedPct = totals.billableRequests > 0
    ? ((totals.verifiedRequests / totals.billableRequests) * 100).toFixed(1)
    : '0.0';

  /**
   * Get max value for progress bars
   */
  const getMaxValue = (data) => {
    if (!data) return 1;
    const values = Object.values(data);
    return Math.max(...values, 1);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs and Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate('/usage')}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <ArrowBackIcon fontSize="small" />
            Usage Analytics
          </Link>
          <Typography color="text.primary">{projectId}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4">Project Usage Details</Typography>
            <Typography variant="body2" color="text.secondary" fontFamily="monospace">
              {projectId}
            </Typography>
          </Box>
          <Tooltip title="Refresh data">
            <span>
              <IconButton onClick={fetchDetails} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            type="date"
            label="Start Date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ minWidth: 150 }}
          />
          <TextField
            type="date"
            label="End Date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ minWidth: 150 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Environment</InputLabel>
            <Select
              value={environment}
              label="Environment"
              onChange={(e) => setEnvironment(e.target.value)}
            >
              <MenuItem value="prod">Production</MenuItem>
              <MenuItem value="stage">Staging</MenuItem>
              <MenuItem value="dev">Development</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Error display */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Loading state */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TrendingUpIcon color="primary" />
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Requests
                    </Typography>
                  </Box>
                  <Typography variant="h4">
                    {formatNumber(totals.totalRequests)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    all traffic incl. bots &amp; errors
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ReceiptIcon color="success" />
                    <Typography variant="subtitle2" color="text.secondary">
                      Billable Impressions
                    </Typography>
                  </Box>
                  <Typography variant="h4" color="success.main">
                    {formatNumber(totals.billableRequests)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    unique visitors per funnel · billed unit
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card data-testid="usage-card-verified">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <VerifiedUserIcon color="info" />
                    <Typography variant="subtitle2" color="text.secondary">
                      JS-Verified
                    </Typography>
                    <ShadowTag />
                  </Box>
                  <Typography variant="h4" color="info.main">
                    {formatNumber(totals.verifiedRequests)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {verifiedPct}% of billable · future billing criterion
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PeopleIcon color="action" />
                    <Typography variant="subtitle2" color="text.secondary">
                      Unique Users
                    </Typography>
                  </Box>
                  <Typography variant="h4">
                    {formatNumber(totals.uniqueUsers)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    all funnels, incl. non-billable
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Traffic Quality */}
          <TrafficQualityPanel details={details} />

          {/* Daily Stats Table */}
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">Daily Statistics</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Requests</TableCell>
                    <TableCell align="right">Billable</TableCell>
                    <TableCell align="right" data-testid="usage-daily-th-verified">
                      Verified<ShadowTag />
                    </TableCell>
                    <TableCell align="right">Users</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(!details?.dailyStats || details.dailyStats.length === 0) ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          No daily data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    details.dailyStats.map((day) => (
                      <TableRow key={day.date}>
                        <TableCell>{day.date}</TableCell>
                        <TableCell align="right">{formatNumber(day.requests)}</TableCell>
                        <TableCell align="right">{formatNumber(day.billableRequests)}</TableCell>
                        <TableCell align="right">{formatNumber(day.verified)}</TableCell>
                        <TableCell align="right">{formatNumber(day.uniqueUsers)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Breakdown Cards */}
          <Grid container spacing={3}>
            {/* By Platform */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <DevicesIcon color="primary" />
                  <Typography variant="h6">By Platform</Typography>
                </Box>
                {(!details?.byPlatform || Object.keys(details.byPlatform).length === 0) ? (
                  <Typography color="text.secondary">No platform data</Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {Object.entries(details.byPlatform)
                      .sort(([, a], [, b]) => b - a)
                      .map(([platform, count]) => (
                        <Box key={platform}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2">{platform || 'Unknown'}</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {formatNumber(count)}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={(count / getMaxValue(details.byPlatform)) * 100}
                            sx={{ height: 6, borderRadius: 1 }}
                          />
                        </Box>
                      ))}
                  </Stack>
                )}
              </Paper>
            </Grid>

            {/* By Country */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PublicIcon color="primary" />
                  <Typography variant="h6">By Country</Typography>
                </Box>
                {(!details?.byCountry || Object.keys(details.byCountry).length === 0) ? (
                  <Typography color="text.secondary">No country data</Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {Object.entries(details.byCountry)
                      .sort(([, a], [, b]) => b - a)
                      .slice(0, 10) // Top 10 countries
                      .map(([country, count]) => (
                        <Box key={country}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2">{country || 'Unknown'}</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {formatNumber(count)}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={(count / getMaxValue(details.byCountry)) * 100}
                            color="secondary"
                            sx={{ height: 6, borderRadius: 1 }}
                          />
                        </Box>
                      ))}
                  </Stack>
                )}
              </Paper>
            </Grid>

            {/* Top Funnels */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LayersIcon color="primary" />
                  <Typography variant="h6">Top Funnels</Typography>
                </Box>
                {(!details?.topFunnels || details.topFunnels.length === 0) ? (
                  <Typography color="text.secondary">No funnel data</Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {details.topFunnels.slice(0, 10).map((funnel, index) => (
                      <Box key={funnel.alias || index}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '70%',
                            }}
                          >
                            {funnel.alias || 'Unknown'}
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {formatNumber(funnel.requests)}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(funnel.requests / (details.topFunnels[0]?.requests || 1)) * 100}
                          color="success"
                          sx={{ height: 6, borderRadius: 1 }}
                        />
                      </Box>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
